import React, { useEffect, useState } from "react";
import {
  FlexGrid,
  FlexGridProps,
  Text,
  theme,
} from "@clearabee/ui-library-native";
import { Input } from "components/Core/Input/Input";
import { useFormContext } from "lib/hooks/useFormContext";
import { FindAddressResult, lookupAddress } from "lib/api/findAddressIo";
import { useTranslation } from "react-i18next";
import { FieldComponentProps } from "lib/formTypes";
import { Button } from "components/Core/Button/Button";
import { useMutation } from "react-query";

interface AddressFieldProps
  extends Pick<
      FieldComponentProps,
      "labelStyles" | "errorBackground" | "errorStyles" | "invertErrorColors"
    >,
    Pick<FlexGridProps, "cellSpacing"> {
  prefix: string;
  disablePostcode?: boolean;
  resetKey?: number;
}

export const AddressField = ({
  prefix: initialPrefix,
  disablePostcode = false,
  resetKey,
  cellSpacing,
  ...fieldProps
}: AddressFieldProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { Field, setValue, watch, getValues } = useFormContext();
  const prefix = initialPrefix ? `${initialPrefix}.` : "";
  const watchedPostcode = watch(`${prefix}postcode`);
  const [houseNumber, setHouseNumber] = useState("");
  const [lookupPostcode, setLookupPostcode] = useState("");
  const [error, setError] = useState("");

  // mutation to find address
  const { mutateAsync: findAddress, isLoading } =
    useMutation<FindAddressResult | null>(
      async () => {
        const postcodeToUse = disablePostcode
          ? getValues(`${prefix}postcode`)
          : lookupPostcode;

        if (!houseNumber.trim() || !postcodeToUse.trim()) {
          setError("Please enter house number and postcode");
          return null;
        }

        const result = await lookupAddress(
          houseNumber.trim(),
          postcodeToUse.trim(),
        );

        if (!result) {
          setError(translate("common.form.errors.addressNotFound"));
          return null;
        }
        return result;
      },
      {
        onSuccess: async (result) => {
          if (!result) return;

          const line1 = [result.house, result.street].filter(Boolean).join(" ");

          setValue(`${prefix}line1`, line1, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue(`${prefix}line2`, result.locality || "", {
            shouldValidate: true,
          });
          setValue(`${prefix}city`, result.town || "", {
            shouldValidate: true,
          });
          setValue(`${prefix}county`, result.county || "", {
            shouldValidate: true,
          });
          setValue(`${prefix}postcode`, result.postcode || "", {
            shouldValidate: true,
          });

          if (result.latitude && result.longitude) {
            setValue(`${prefix}lat`, result.latitude);
            setValue(`${prefix}lng`, result.longitude);
          }
        },
        onError: () => {
          setError("Address lookup failed");
        },
      },
    );

  // Reset postcode lookup when parent increments resetKey (e.g. after putAddresses)
  useEffect(() => {
    if (resetKey === undefined) return;

    setHouseNumber("");
    setLookupPostcode("");
    setError("");
    setValue(`${prefix}lat`, null);
    setValue(`${prefix}lng`, null);
    setValue(`${prefix}line1`, "");
    setValue(`${prefix}line2`, "");
    setValue(`${prefix}city`, "");
    setValue(`${prefix}county`, "");
    setValue(`${prefix}postcode`, "");
  }, [resetKey, prefix, setValue]);

  return (
    <>
      <FlexGrid
        cellSpacing={cellSpacing}
        rowSpacing={null}
        style={{
          backgroundColor: theme.colors.light.base,
          paddingVertical: 24,
          paddingHorizontal: theme.spacing.small,
          borderRadius: 4,
          marginHorizontal: 2,
        }}
      >
        {/* House Number */}
        <FlexGrid.Cell
          base="100%"
          xsmall="40%"
          style={{ paddingHorizontal: 4 }}
        >
          <Text fontSize="small">{translate("common.houseNumberOrName")}</Text>
          <Input.Text
            name="houseNumber"
            value={houseNumber}
            onChange={(value) => {
              if (error) setError("");
              setHouseNumber(value);
            }}
            placeholder="e.g. 10"
            disabled={isLoading}
          />
        </FlexGrid.Cell>

        {/* Postcode */}
        <FlexGrid.Cell
          base="100%"
          xsmall="37%"
          style={{ paddingHorizontal: 4 }}
        >
          <Text fontSize="small">{translate("common.postcode")}</Text>
          <Input.Text
            name="lookupPostcode"
            value={disablePostcode ? watchedPostcode : lookupPostcode}
            onChange={(value) => {
              if (error) setError("");
              setLookupPostcode(value);
            }}
            placeholder="SW1A 2AA"
            disabled={disablePostcode || isLoading}
          />
        </FlexGrid.Cell>

        {/* Find Address Button */}
        <FlexGrid.Cell
          base="100%"
          xsmall="23%"
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            paddingHorizontal: 4,
          }}
        >
          <Button
            type="button"
            color="warning"
            size="small"
            onClick={() => findAddress()}
            disabled={isLoading}
          >
            {isLoading
              ? translate("common.searching")
              : translate("common.findAddress")}
          </Button>
        </FlexGrid.Cell>

        {/* Error Message */}
        {error && (
          <FlexGrid.Cell
            base="100%"
            style={{ paddingHorizontal: 4, paddingTop: 4 }}
          >
            <Text fontSize="xsmall" color="negative">
              {error}
            </Text>
          </FlexGrid.Cell>
        )}
      </FlexGrid>

      <FlexGrid cellSpacing={cellSpacing} rowSpacing={null}>
        <FlexGrid.Cell base="100%" xsmall="50%">
          <Field
            name={`${prefix}line1`}
            label={translate("common.addressLine1")}
            required
            {...fieldProps}
          >
            {({ field }) => (
              <Input.Text
                {...field}
                placeholder={translate("common.form.placeholder.addressLine1")}
              />
            )}
          </Field>
        </FlexGrid.Cell>
        <FlexGrid.Cell base="100%" xsmall="50%">
          <Field
            name={`${prefix}line2`}
            label={translate("common.addressLine2")}
            {...fieldProps}
          >
            {({ field }) => (
              <Input.Text
                {...field}
                placeholder={translate("common.form.placeholder.addressLine2")}
              />
            )}
          </Field>
        </FlexGrid.Cell>
        <FlexGrid.Cell base="100%" xsmall="50%">
          <Field
            name={`${prefix}city`}
            label={translate("common.city")}
            required
            {...fieldProps}
          >
            {({ field }) => (
              <Input.Text
                {...field}
                placeholder={translate("common.form.placeholder.city")}
              />
            )}
          </Field>
        </FlexGrid.Cell>
        <FlexGrid.Cell base="100%" xsmall="50%">
          <Field
            name={`${prefix}county`}
            label={translate("common.county")}
            {...fieldProps}
          >
            {({ field }) => (
              <Input.Text
                {...field}
                placeholder={translate("common.form.placeholder.county")}
              />
            )}
          </Field>
        </FlexGrid.Cell>
        <FlexGrid.Cell base="100%" xsmall="50%">
          <Field
            name={`${prefix}postcode`}
            label={translate("common.postcode")}
            required
            {...fieldProps}
          >
            {({ field }) => (
              <Input.Text
                {...field}
                disabled={disablePostcode}
                placeholder={translate("common.form.placeholder.postcode")}
                onChange={(e) => {
                  field.onChange(e);
                  setValue(`${prefix}lat`, null);
                  setValue(`${prefix}lng`, null);
                }}
              />
            )}
          </Field>
        </FlexGrid.Cell>
      </FlexGrid>
    </>
  );
};
